import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ArrowRight, CheckCircle2, Clock, ExternalLink, FilePlus2, Loader2, ShieldCheck, X } from 'lucide-react';
import { readOnlyMessage, readOnlyTitle } from '../../services/readOnlyNotice';
import { AUSGABE_UNTERLAGE_LABEL, type Abweichung, type AbweichungsStand } from '../../services/projektSteckbrief/abweichungen';
import type { AusgabeUnterlage, FassungsEntscheid } from '../../services/unterlagenAusgabenService';
import { entscheidKennung } from '../common/abweichungsHinweisLogik';
import { ANLASS_TEXT, fassungText, pruefeRueckfrageBegruendung } from './uebergabeLogic';
import { datumDeutsch } from './unterlagenLogic';

/** Die beiden Antworten, die gespeichert werden — „Später" speichert nichts. */
export type RueckfrageEntscheid = Exclude<FassungsEntscheid, 'spaeter'>;

interface Props {
    /** Die offenen Staende beim Oeffnen, in dieser Reihenfolge nacheinander im Dialog. */
    staende: readonly AbweichungsStand[];
    projektName?: string;
    projektNummer?: string;
    isReadOnly: boolean;
    /**
     * Gesetzt, solange der Aenderungsdienst nicht vollstaendig gelesen ist:
     * die Knoepfe, die schreiben, sind dann gesperrt, der Grund steht im
     * Dialog. Blaettern und Schliessen gehen weiter.
     */
    sperrgrund?: string | null;
    /** Haelt den Entscheid fest; liefert den Fehlersatz oder `null` bei Erfolg. */
    onEntscheiden: (stand: AbweichungsStand, entscheid: RueckfrageEntscheid, begruendung: string | null) => Promise<string | null>;
    /** Sprung ins Modul der Unterlage — der Dialog schliesst sich davor selbst. */
    onZumModul: (unterlage: AusgabeUnterlage) => void;
    /** Schliessen ohne Entscheid = wie „Später": nichts gespeichert. */
    onSchliessen: () => void;
}

type Schritt = 'wahl' | 'begruendung' | 'neue_fassung';

const FELD = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50';
const KNOPF = 'inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg border disabled:opacity-40 disabled:cursor-not-allowed';

/**
 * Der Aenderungsdienst kennzeichnet „nichts" mit einem Strich. In einer
 * Zeile, die ein Mensch liest („Kran: — → ja"), ist das ein Raetsel; hier
 * steht das Wort.
 */
const wertText = (t: string): string => {
    const rein = (t || '').trim();
    return !rein || rein === '—' || rein === '-' ? 'nicht angegeben' : rein;
};

/**
 * Rueckfrage nach dem Speichern der Grundlagen (Plan Stufe 6.1, „Rückfrage
 * statt Automatik"): fuer jede Unterlage mit freigegebener oder versendeter
 * Fassung, deren Planungsdaten jetzt abweichen, fragt der Dialog, was
 * geschehen soll — eine Unterlage nach der anderen, im selben Dialog.
 *
 *   Neue Fassung anlegen   — Entscheid wird gespeichert; danach der Hinweis,
 *                            im Modul eine neue Fassung als Entwurf zu
 *                            erstellen, mit Sprung ins Modul. Die geltende
 *                            Fassung bleibt gueltig, bis die neue freigegeben
 *                            ist.
 *   Nicht erheblich        — mit Pflichtbegruendung: der Nachweis, dass der
 *                            Koordinator die Aenderung bewertet hat. Fuer
 *                            GENAU diese Aenderung wird nicht erneut gefragt;
 *                            eine weitere Aenderung fragt neu.
 *   Später                 — nichts gespeichert, die Frage bleibt offen
 *                            (gelb im Reiter „Übergabe"). Schliessen ohne
 *                            Entscheid wirkt auf alle verbleibenden ebenso.
 *
 * ═══ KEIN RIEGEL ══════════════════════════════════════════════════════════
 * Gespeichert sind die Grundlagen laengst, wenn der Dialog erscheint (Muster
 * Datenpflege-Popup). Er haelt nichts auf und kann jederzeit zugeklappt
 * werden. In der Nur-Ansicht zeigt er die Abweichungen, die Knoepfe, die
 * schreiben, sind gesperrt — Blaettern und Schliessen gehen.
 *
 * ═══ SCHREIBT NUR PER KLICK, UEBER `onEntscheiden` ════════════════════════
 * Der Dialog kennt weder Tabelle noch Dienst. Was er speichert, geht ueber
 * den einen Rueckruf; dessen Fehlersatz bleibt im Dialog stehen, bis der
 * Mensch ihn gesehen hat. Ein Erfolg schaltet weiter. Je Kennung (Ausgabe-Id
 * + Hash) wird hoechstens EIN Entscheid geschickt: `laufRef` sperrt, solange
 * einer unterwegs ist, `erledigtRef` danach — derselbe Riegel wie im Banner
 * (`AbweichungsHinweis`), damit kein Doppelklick doppelt speichert.
 *
 * Mit `sperrgrund` (Aenderungsdienst nicht vollstaendig gelesen) sind die
 * schreibenden Knoepfe gesperrt: ein Stand aus Ersatzwerten bekommt keinen
 * Entscheid.
 *
 * Per Portal an `document.body`, wie `VersandDialog`: der Modulinhalt
 * scrollt in einem eigenen Container, ein `fixed`-Dialog darin haengt am
 * naechsten Vorfahren mit `transform`.
 */
export const FassungsRueckfrage: React.FC<Props> = ({
    staende, projektName, projektNummer, isReadOnly, sperrgrund = null, onEntscheiden, onZumModul, onSchliessen,
}) => {
    const [index, setIndex] = useState(0);
    const [schritt, setSchritt] = useState<Schritt>('wahl');
    const [begruendung, setBegruendung] = useState('');
    const [begruendungFehler, setBegruendungFehler] = useState<string | null>(null);
    const [fehler, setFehler] = useState<string | null>(null);
    const [laeuft, setLaeuft] = useState(false);
    const [festgehalten, setFestgehalten] = useState(0);
    // Doppelklick-Riegel wie im Modul: `laeuft` greift erst nach dem naechsten Zeichnen.
    const laufRef = useRef(false);
    // Kennungen (Ausgabe-Id + Hash), deren Entscheid gespeichert ist — ein
    // zweiter Klick darauf schreibt nichts mehr.
    const erledigtRef = useRef<Set<string>>(new Set());

    const aktuell = staende[index];
    const letzte = index + 1 >= staende.length;
    const schreibsperre = (sperrgrund || '').trim() || null;
    const gesperrt = laeuft || isReadOnly || !!schreibsperre;

    /** Zur naechsten Unterlage — oder zu, wenn es die letzte war. */
    const weiter = () => {
        if (laeuft) return;
        if (letzte) { onSchliessen(); return; }
        setIndex(i => i + 1);
        setSchritt('wahl');
        setBegruendung('');
        setBegruendungFehler(null);
        setFehler(null);
    };

    const entscheide = async (entscheid: RueckfrageEntscheid, text: string | null): Promise<boolean> => {
        if (!aktuell || laufRef.current) return false;
        const kennung = entscheidKennung(aktuell);
        if (erledigtRef.current.has(kennung)) return false;
        if (isReadOnly) {
            setFehler(readOnlyMessage('eine Fassungsentscheidung festzuhalten'));
            return false;
        }
        if (schreibsperre) {
            setFehler(schreibsperre);
            return false;
        }
        laufRef.current = true;
        setLaeuft(true);
        setFehler(null);
        try {
            const fehlersatz = await onEntscheiden(aktuell, entscheid, text);
            if (fehlersatz) { setFehler(fehlersatz); return false; }
            erledigtRef.current.add(kennung);
            setFestgehalten(n => n + 1);
            return true;
        } catch (e) {
            setFehler(e instanceof Error ? e.message : String(e));
            return false;
        } finally {
            laufRef.current = false;
            setLaeuft(false);
        }
    };

    const neueFassung = () => {
        void entscheide('neue_fassung', null).then(ok => { if (ok) setSchritt('neue_fassung'); });
    };

    const nichtErheblichFesthalten = () => {
        const pruefung = pruefeRueckfrageBegruendung(begruendung);
        setBegruendungFehler(pruefung.fehler);
        if (!pruefung.wert) return;
        void entscheide('nicht_erheblich', pruefung.wert).then(ok => { if (ok) weiter(); });
    };

    const zumModul = () => {
        if (!aktuell) return;
        const unterlage = aktuell.unterlage;
        onSchliessen();
        onZumModul(unterlage);
    };

    if (!aktuell) return null;

    const ausgabe = aktuell.ausgabe;
    const anlass = ANLASS_TEXT[ausgabe.anlass] || ausgabe.anlass;
    const tag = datumDeutsch(ausgabe.erstellt_am);
    const projekt = [projektName, projektNummer].filter(Boolean).join(' · ');

    return createPortal(
        <div className="fixed inset-0 z-[80] bg-slate-900/50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col min-h-0">

                <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-200">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <h2 className="font-black text-slate-900">Geltende Fassung prüfen</h2>
                        <p className="text-xs text-slate-500">
                            Unterlage {index + 1} von {staende.length}
                            {projekt && <> · {projekt}</>}
                            {' '}· Die Grundlagen sind gespeichert; hier geht es nur um die Folgeunterlagen.
                        </p>
                    </div>
                    <button type="button" onClick={onSchliessen} className="p-1 text-slate-400 hover:text-slate-600" aria-label="Schließen">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto min-h-0 px-5 py-4 space-y-4">
                    {isReadOnly && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-900">
                            {readOnlyTitle('Entscheidungen')} Die Abweichungen sind trotzdem sichtbar.
                        </div>
                    )}
                    {schreibsperre && !isReadOnly && (
                        <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-700">
                            {schreibsperre} Die gezeigten Abweichungen stammen aus dem letzten vollständigen Lesen; entschieden
                            werden kann erst wieder, wenn der Änderungsdienst lesbar ist.
                        </div>
                    )}
                    {fehler && (
                        <div className="bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 text-xs text-rose-900 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>{fehler}</span>
                        </div>
                    )}

                    {/* ─── Unterlage und Fassung ───────────────────────── */}
                    <section>
                        <h3 className="text-lg font-black text-slate-900">{AUSGABE_UNTERLAGE_LABEL[aktuell.unterlage]}</h3>
                        <p className="text-xs text-slate-600">
                            {fassungText(ausgabe)} · {anlass}{tag ? ` am ${tag}` : ''}
                            {ausgabe.erstellt_von_name && <> von {ausgabe.erstellt_von_name}</>}
                        </p>
                    </section>

                    {/* ─── Was sich aendert ────────────────────────────── */}
                    <section>
                        <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2">
                            Was sich gegenüber dieser Fassung ändert ({aktuell.abweichungen.length})
                        </h4>
                        {aktuell.abweichungen.length === 0 ? (
                            <p className="text-xs text-slate-500">
                                Der Vergleich nennt keine einzelne Angabe; die Fassung gilt trotzdem als abweichend.
                            </p>
                        ) : (
                            <ul className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-56 overflow-y-auto">
                                {aktuell.abweichungen.map((a: Abweichung) => (
                                    <li key={a.pfad} className="px-3 py-2 text-xs flex items-start gap-2">
                                        <span className="font-bold text-slate-700 shrink-0">{a.label || a.pfad}:</span>
                                        <span className="text-slate-500 line-through decoration-slate-300 min-w-0 break-words">{wertText(a.altText)}</span>
                                        <ArrowRight className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" aria-label="wird zu" />
                                        <span className="font-bold text-slate-900 min-w-0 break-words">{wertText(a.neuText)}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    {/* ─── Schritt ─────────────────────────────────────── */}
                    {schritt === 'wahl' && (
                        <section className="space-y-2">
                            <p className="text-xs text-slate-600 leading-snug">
                                Nach BaustellV ist die Unterlage bei erheblichen Änderungen anzupassen. Die geltende Fassung bleibt
                                unverändert gültig, bis eine neue freigegeben ist. Was soll geschehen?
                            </p>
                            <div className="flex items-center gap-2 flex-wrap">
                                <button
                                    type="button"
                                    onClick={neueFassung}
                                    disabled={gesperrt}
                                    title={isReadOnly
                                        ? readOnlyTitle('Neue Fassung')
                                        : schreibsperre || 'Entscheid festhalten und im Modul eine neue Fassung als Entwurf erstellen'}
                                    className={`${KNOPF} text-white bg-blue-600 border-blue-600 hover:bg-blue-700`}
                                >
                                    {laeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FilePlus2 className="w-3.5 h-3.5" />}
                                    Neue Fassung anlegen
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setSchritt('begruendung'); setFehler(null); }}
                                    disabled={gesperrt}
                                    title={isReadOnly
                                        ? readOnlyTitle('Nicht erheblich')
                                        : schreibsperre || 'Mit Begründung festhalten, dass keine neue Fassung nötig ist'}
                                    className={`${KNOPF} text-slate-700 bg-white border-slate-300 hover:bg-slate-50`}
                                >
                                    <ShieldCheck className="w-3.5 h-3.5" /> Keine neue Fassung: nicht erheblich
                                </button>
                                <button
                                    type="button"
                                    onClick={weiter}
                                    disabled={laeuft}
                                    title={'Nichts speichern; die Frage bleibt offen und wird im Reiter „Übergabe" gezeigt'}
                                    className={`${KNOPF} text-slate-600 bg-white border-slate-200 hover:bg-slate-50`}
                                >
                                    <Clock className="w-3.5 h-3.5" /> Später
                                </button>
                            </div>
                        </section>
                    )}

                    {schritt === 'begruendung' && (
                        <section className="space-y-2">
                            <label className="block">
                                <span className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                    Begründung: warum ist die Änderung für diese Unterlage nicht erheblich?
                                </span>
                                <textarea
                                    value={begruendung}
                                    disabled={gesperrt}
                                    onChange={ev => { setBegruendung(ev.target.value); setBegruendungFehler(null); }}
                                    rows={3}
                                    autoFocus
                                    className={`${FELD} leading-relaxed`}
                                    placeholder="Ein Satz, der später nachvollziehbar ist."
                                />
                                <span className="block text-[11px] text-slate-400 mt-1">
                                    Festgehalten werden wer, wann, welche Änderung und diese Begründung. Für genau diese Änderung wird
                                    nicht erneut gefragt; eine weitere Änderung fragt neu.
                                </span>
                            </label>
                            {begruendungFehler && (
                                <p className="text-xs text-rose-700 font-bold">{begruendungFehler}</p>
                            )}
                            <div className="flex items-center gap-2 flex-wrap">
                                <button
                                    type="button"
                                    onClick={nichtErheblichFesthalten}
                                    disabled={gesperrt}
                                    title={isReadOnly ? readOnlyTitle('Festhalten') : schreibsperre || undefined}
                                    className={`${KNOPF} text-white bg-slate-800 border-slate-800 hover:bg-slate-900`}
                                >
                                    {laeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                                    Nicht erheblich: festhalten
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setSchritt('wahl'); setBegruendungFehler(null); }}
                                    disabled={laeuft}
                                    className={`${KNOPF} text-slate-600 bg-white border-slate-200 hover:bg-slate-50`}
                                >
                                    Zurück
                                </button>
                            </div>
                        </section>
                    )}

                    {schritt === 'neue_fassung' && (
                        <section className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-900 space-y-2">
                            <div className="flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                                <div>
                                    <div className="font-bold">Entscheid festgehalten: neue Fassung.</div>
                                    <p className="text-xs leading-snug">
                                        Im Modul „{AUSGABE_UNTERLAGE_LABEL[aktuell.unterlage]}" eine neue Fassung als Entwurf erstellen.
                                        Die geltende Fassung bleibt gültig, bis die neue freigegeben ist; die Änderungen erscheinen dort
                                        als Vorschläge aus der Planungsphase.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <button
                                    type="button"
                                    onClick={zumModul}
                                    className={`${KNOPF} text-white bg-emerald-700 border-emerald-700 hover:bg-emerald-800`}
                                >
                                    <ExternalLink className="w-3.5 h-3.5" /> Zum Modul
                                </button>
                                <button
                                    type="button"
                                    onClick={weiter}
                                    className={`${KNOPF} text-emerald-900 bg-white border-emerald-300 hover:bg-emerald-100`}
                                >
                                    {letzte ? 'Schließen' : 'Nächste Unterlage'}
                                </button>
                            </div>
                        </section>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-3 text-[11px] text-slate-500">
                    <span className="flex-1 min-w-0">
                        {festgehalten > 0
                            ? `${festgehalten} Entscheid${festgehalten === 1 ? '' : 'e'} festgehalten.`
                            : 'Noch nichts festgehalten.'}
                        {' '}Schließen ohne Entscheid lässt die Frage offen; gespeichert wird dabei nichts.
                    </span>
                    <button
                        type="button"
                        onClick={onSchliessen}
                        disabled={laeuft}
                        className="px-3 py-2 text-xs font-bold text-slate-600 hover:text-slate-800 disabled:opacity-40"
                    >
                        Schließen
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};
